/**
 * Forecast
 * 
 * Probabilistic finish date ranges with key drivers and distribution shifts.
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, TrendingUp, AlertCircle } from 'lucide-react';
import { DarkPanel } from '@/components/ui/DarkPanel';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { API_BASE_URL } from '@/config';
import { getAuthHeaders } from '@/api/workosAuth';
import MainLayout from '@/components/layout/MainLayout';
import { EmptySnapshotState } from '@/components/project-state/EmptySnapshotState';

export const Forecast: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { theme } = useAppTheme();
  const [forecastData, setForecastData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (projectId) {
      // For now, use snapshot metadata for basic forecast
      fetchSnapshotData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchSnapshotData recreated each render; projectId is key
  }, [projectId]);

  const fetchSnapshotData = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/v1/snapshots/${projectId}/latest`, {
        credentials: 'include',
        headers,
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.requires_upload) {
          setForecastData(null);
          return;
        }
        throw new Error(errorData.message || 'Failed to fetch snapshot');
      }
      const data = await response.json();
      
      // Check if this is an error/message response (no actual snapshot data)
      if (data.requires_upload || (!data.id && data.message)) {
        setForecastData(null);
        return;
      }
      
      // Create basic forecast from snapshot metadata
      if (data.extended_metadata) {
        setForecastData({
          project_finish_date: data.extended_metadata.project_finish_date,
          project_start_date: data.extended_metadata.project_start_date,
          total_activities: data.extended_metadata.total_activities,
          critical_activities: data.extended_metadata.critical_activities,
          negative_float_count: data.extended_metadata.negative_float_count
        });
      } else if (data.metadata) {
        // Fallback to basic metadata
        setForecastData({
          total_activities: data.metadata?.total_activities || 0,
          critical_activities: data.metadata?.critical_activities || 0,
          negative_float_count: data.metadata?.negative_float_count || 0
        });
      }
    } catch (err) {
      console.error('Failed to load forecast:', err);
      setForecastData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout title="Forecast">
      {loading ? (
        <div className="p-8 text-center">Loading forecast...</div>
      ) : !forecastData ? (
        <EmptySnapshotState
          title="No Forecast Data Available"
          message="Upload a XER file to create a snapshot and generate forecast data."
        />
      ) : (
        <div className="p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            <DarkPanel className="p-6">
              <h2 className="text-lg font-semibold mb-4">Project Finish Date</h2>
              <div className="flex items-center gap-4">
                <Calendar className="h-8 w-8 text-blue-500" />
                <div>
                  <div className="text-2xl font-bold">
                    {forecastData.project_finish_date
                      ? new Date(forecastData.project_finish_date).toLocaleDateString()
                      : 'Not available'}
                  </div>
                  <div className="text-sm text-gray-500">Expected completion</div>
                </div>
              </div>
            </DarkPanel>

            <div className="grid grid-cols-3 gap-4">
              <DarkPanel className="p-4">
                <div className="text-sm text-gray-500 mb-1">Total Activities</div>
                <div className="text-2xl font-bold">{forecastData.total_activities || 0}</div>
              </DarkPanel>
              <DarkPanel className="p-4">
                <div className="text-sm text-gray-500 mb-1">Critical Activities</div>
                <div className="text-2xl font-bold text-red-500">{forecastData.critical_activities || 0}</div>
              </DarkPanel>
              <DarkPanel className="p-4">
                <div className="text-sm text-gray-500 mb-1">Negative Float</div>
                <div className="text-2xl font-bold text-red-500">{forecastData.negative_float_count || 0}</div>
              </DarkPanel>
            </div>

            <DarkPanel className="p-6">
              <h2 className="text-lg font-semibold mb-4">Key Drivers</h2>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <span>Critical path activities: {forecastData.critical_activities || 0}</span>
                </div>
                {forecastData.negative_float_count > 0 && (
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-red-500" />
                    <span>Activities with negative float: {forecastData.negative_float_count}</span>
                  </div>
                )}
              </div>
            </DarkPanel>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default Forecast;

